import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { ScrollArea } from "../components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { motion } from "framer-motion";
import { 
  Search, Download, FileText, ChevronLeft, ChevronRight, X, 
  Paperclip, Eye, Upload, Pencil, Sparkles, Trash2, CheckSquare, Square,
  Landmark, ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import ImportModal from "../components/ImportModal";
import PDFViewerModal from "../components/PDFViewerModal";
import InvoiceEditModal from "../components/InvoiceEditModal";
import OCRUploadModal from "../components/OCRUploadModal";
import DeleteInvoiceDialog from "../components/DeleteInvoiceDialog";
import AxonautSyncButton from "../components/AxonautSyncButton";
import { MultiSelectFilter } from "../components/ui/multi-select-filter";
import BankingStatusBadge from "../components/ui/banking-status-badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  // Handle null, undefined, or NaN values
  if (value === null || value === undefined || isNaN(value)) {
    return '0,00 €';
  }
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  
  try {
    let date;
    
    // Handle different date formats
    if (typeof dateStr === 'string') {
      // Format: DD/MM/YYYY
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const [day, month, year] = parts;
          date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
      }
      // Format: YYYY-MM-DD or ISO
      else if (dateStr.includes('-')) {
        date = new Date(dateStr);
      }
      // Try direct parsing
      else {
        date = new Date(dateStr);
      }
    } else {
      date = new Date(dateStr);
    }
    
    // Check if date is valid
    if (!date || isNaN(date.getTime())) {
      return '-';
    }
    
    return date.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
  } catch (e) {
    return '-';
  }
};

export default function Factures() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(50);
  
  // Batch selection
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Filters - now arrays for multi-select
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState([]);
  const [yearFilter, setYearFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [paiementFilter, setPaiementFilter] = useState("all");
  const [allClients, setAllClients] = useState([]);
  
  // Statuts de paiement
  const paiementStatuts = [
    { value: "all", label: "Tous" },
    { value: "payee", label: "Payée" },
    { value: "rejetee", label: "Rejetée" },
    { value: "en_attente", label: "En attente" },
  ];
  
  // Catégories prédéfinies
  const categories = [
    { id: "hebergement", label: "Hébergement / Sauvegarde" },
    { id: "fibre", label: "Fibre / Internet" },
    { id: "telephonie", label: "Téléphonie" },
    { id: "materiel", label: "Matériel / Consommables" },
    { id: "installation", label: "Installation / Mise en service" },
    { id: "maintenance", label: "Maintenance / Support" },
    { id: "logiciel", label: "Logiciels / Licences" },
    { id: "commission", label: "Commissions" },
    { id: "formation", label: "Formation" },
    { id: "conseil", label: "Conseil / Audit" },
  ];
  
  // Detail sheet
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  
  // Import modals
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [ocrUploadOpen, setOcrUploadOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const years = ['2020', '2021', '2022', '2023', '2024', '2025', '2026'];
  const months = [
    { value: '01', label: 'Janvier' },
    { value: '02', label: 'Février' },
    { value: '03', label: 'Mars' },
    { value: '04', label: 'Avril' },
    { value: '05', label: 'Mai' },
    { value: '06', label: 'Juin' },
    { value: '07', label: 'Juillet' },
    { value: '08', label: 'Août' },
    { value: '09', label: 'Septembre' },
    { value: '10', label: 'Octobre' },
    { value: '11', label: 'Novembre' },
    { value: '12', label: 'Décembre' }
  ];

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit === 'all' ? 10000 : limit);
      if (search) params.append('search', search);
      // Multi-select filters - join values with comma
      if (typeFilter.length > 0) params.append('type', typeFilter.join(','));
      if (yearFilter !== 'all') params.append('year', yearFilter);
      if (monthFilter !== 'all') params.append('month', monthFilter);
      if (clientFilter.length > 0) params.append('client', clientFilter.join(','));
      if (categoryFilter.length > 0) params.append('category', categoryFilter.join(','));
      if (paiementFilter !== 'all') params.append('statut_paiement', paiementFilter);
      
      const res = await axios.get(`${API}/invoices?${params.toString()}`, { headers });
      setInvoices(res.data.invoices);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
      toast.error("Erreur lors du chargement des factures");
    } finally {
      setLoading(false);
    }
  }, [token, page, limit, search, typeFilter, yearFilter, monthFilter, clientFilter, categoryFilter, paiementFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Fetch all clients for filter dropdown (deduplicated)
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/stats/unique-clients`, { headers });
        // Use normalized value for filtering, display label for UI
        setAllClients(res.data.map(c => ({
          value: c.value,
          label: `${c.label} (${c.count})`
        })));
      } catch (error) {
        console.error("Failed to fetch clients:", error);
        // Fallback to old endpoint
        try {
          const res = await axios.get(`${API}/stats/top-clients?limit=500`, { headers });
          setAllClients([...new Set(res.data.map(c => c.client))].sort().map(c => ({ value: c, label: c })));
        } catch (e) {
          console.error("Fallback also failed:", e);
        }
      }
    };
    fetchClients();
  }, [token]);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, yearFilter, monthFilter, clientFilter, categoryFilter, paiementFilter, limit]);

  // Open invoice detail from URL parameter ?invoice=XXX
  useEffect(() => {
    const invoiceId = searchParams.get('invoice');
    if (invoiceId) {
      const loadInvoiceFromUrl = async () => {
        try {
          const headers = { Authorization: `Bearer ${token}` };
          const res = await axios.get(`${API}/invoices/${encodeURIComponent(invoiceId)}`, { headers });
          if (res.data) {
            setSelectedInvoice(res.data);
            setSheetOpen(true);
          }
        } catch (err) {
          console.error("Failed to load invoice from URL:", err);
        }
      };
      loadInvoiceFromUrl();
    }
  }, [searchParams, token]);

  const handleInvoiceClick = async (invoice) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/invoices/${encodeURIComponent(invoice.numero)}`, { headers });
      setSelectedInvoice(res.data);
      setSheetOpen(true);
    } catch (error) {
      toast.error("Erreur lors du chargement de la facture");
    }
  };

  // Export Excel - respecte la sélection si présente
  const handleExport = async () => {
    try {
      let exportData;
      
      // Si des factures sont sélectionnées, exporter uniquement celles-ci
      if (selectedInvoices.size > 0) {
        // Utiliser les données déjà en mémoire (invoices)
        const selectedData = invoices.filter(inv => selectedInvoices.has(inv.numero));
        exportData = selectedData.map(inv => ({
          'Numéro': inv.numero,
          'Type': inv.type,
          'Client': inv.client,
          'Date': inv.date,
          'Échéance': inv.echeance,
          'Total HT': inv.total_ht,
          'Total TVA': inv.total_tva,
          'Total TTC': inv.total_ttc,
          'Statut paiement': inv.paiement ? 'Payée' : 'Non payée',
          'Date paiement': inv.date_paiement || '',
          'PDF': inv.has_pdf ? 'Oui' : 'Non'
        }));
        toast.success(`${exportData.length} factures sélectionnées exportées`);
      } else {
        // Sinon, exporter les factures actuellement affichées
        exportData = invoices.map(inv => ({
          'Numéro': inv.numero,
          'Type': inv.type,
          'Client': inv.client,
          'Date': inv.date,
          'Échéance': inv.echeance,
          'Total HT': inv.total_ht,
          'Total TVA': inv.total_tva,
          'Total TTC': inv.total_ttc,
          'Statut paiement': inv.paiement ? 'Payée' : 'Non payée',
          'Date paiement': inv.date_paiement || '',
          'PDF': inv.has_pdf ? 'Oui' : 'Non'
        }));
        toast.success(`${exportData.length} factures exportées`);
      }
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Factures");
      
      let filename = 'factures';
      if (selectedInvoices.size > 0) filename += '_selection';
      else if (yearFilter !== 'all') filename += `_${yearFilter}`;
      if (monthFilter !== 'all') filename += `_${monthFilter}`;
      filename += '.xlsx';
      
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Erreur lors de l'export Excel");
    }
  };

  // Export PDF - télécharge les PDFs des factures sélectionnées ou toutes
  const handleExportPDF = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Déterminer les factures à exporter
      let invoicesToExport;
      if (selectedInvoices.size > 0) {
        invoicesToExport = invoices.filter(inv => selectedInvoices.has(inv.numero) && inv.has_pdf);
      } else {
        invoicesToExport = invoices.filter(inv => inv.has_pdf);
      }
      
      if (invoicesToExport.length === 0) {
        toast.error("Aucune facture avec PDF à exporter");
        return;
      }
      
      toast.info(`Export de ${invoicesToExport.length} PDF en cours...`);
      
      // Si une seule facture, télécharger directement
      if (invoicesToExport.length === 1) {
        const inv = invoicesToExport[0];
        const response = await axios.get(`${API}/invoices/${inv.numero}/pdf`, {
          headers,
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `facture_${inv.numero}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('PDF téléchargé');
        return;
      }
      
      // Si plusieurs factures, créer un ZIP
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      let successCount = 0;
      for (const inv of invoicesToExport) {
        try {
          const response = await axios.get(`${API}/invoices/${inv.numero}/pdf`, {
            headers,
            responseType: 'blob'
          });
          zip.file(`facture_${inv.numero}.pdf`, response.data);
          successCount++;
        } catch (e) {
          console.error(`Erreur téléchargement PDF ${inv.numero}:`, e);
        }
      }
      
      if (successCount === 0) {
        toast.error("Aucun PDF n'a pu être récupéré");
        return;
      }
      
      const content = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `factures_${selectedInvoices.size > 0 ? 'selection' : 'export'}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${successCount} PDFs exportés dans le ZIP`);
    } catch (error) {
      console.error("Export PDF error:", error);
      toast.error("Erreur lors de l'export PDF");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setTypeFilter([]);
    setYearFilter("all");
    setMonthFilter("all");
    setClientFilter([]);
    setCategoryFilter([]);
    setPaiementFilter("all");
  };

  const handleViewPDF = () => {
    setPdfViewerOpen(true);
  };

  // Batch selection handlers
  const toggleSelectInvoice = (numero, e) => {
    e.stopPropagation();
    const newSelected = new Set(selectedInvoices);
    if (newSelected.has(numero)) {
      newSelected.delete(numero);
    } else {
      newSelected.add(numero);
    }
    setSelectedInvoices(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedInvoices.size === invoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(invoices.map(inv => inv.numero)));
    }
  };

  const handleBatchDelete = async () => {
    if (selectedInvoices.size === 0) return;
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedInvoices.size} facture(s) ?`)) {
      return;
    }
    
    setIsDeleting(true);
    try {
      const response = await axios.post(
        `${API}/invoices/batch-delete`,
        { numeros: Array.from(selectedInvoices) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${response.data.deleted_count} facture(s) supprimée(s)`);
      
      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.failed_count} facture(s) n'ont pas pu être supprimées`);
      }
      
      setSelectedInvoices(new Set());
      fetchInvoices();
    } catch (error) {
      console.error("Batch delete error:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setIsDeleting(false);
    }
  };

  const hasFilters = search || typeFilter.length > 0 || yearFilter !== 'all' || monthFilter !== 'all' || clientFilter.length > 0 || categoryFilter.length > 0 || paiementFilter !== 'all';

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="factures-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">
            Factures émises
          </h1>
          <p className="text-muted-foreground text-sm">
            {total} facture{total > 1 ? 's' : ''} émise{total > 1 ? 's' : ''}
            {selectedInvoices.size > 0 && (
              <span className="ml-2 text-primary font-medium">
                • {selectedInvoices.size} sélectionnée(s)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedInvoices.size > 0 && (
            <Button 
              variant="destructive"
              onClick={handleBatchDelete}
              disabled={isDeleting}
              data-testid="batch-delete-btn"
              size="sm"
              className="flex-1 sm:flex-none"
            >
              <Trash2 className="w-4 h-4 mr-1 sm:mr-2" />
              {isDeleting ? 'Suppression...' : `Supprimer (${selectedInvoices.size})`}
            </Button>
          )}
          <Button 
            variant="default"
            onClick={() => setOcrUploadOpen(true)}
            data-testid="ocr-upload-btn"
            size="sm"
            className="flex-1 sm:flex-none bg-purple-600 hover:bg-purple-700 text-white"
          >
            <Sparkles className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Import OCR</span>
            <span className="sm:hidden">OCR</span>
          </Button>
          <AxonautSyncButton onSuccess={fetchInvoices} />
          <Button 
            variant="outline"
            onClick={() => setImportModalOpen(true)}
            data-testid="import-excel-btn"
            size="sm"
            className="flex-1 sm:flex-none"
          >
            <Upload className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Importer Excel</span>
            <span className="sm:hidden">Excel</span>
          </Button>
          <Button 
            onClick={handleExport}
            variant="outline"
            className="flex-1 sm:flex-none"
            data-testid="export-btn"
            size="sm"
          >
            <Download className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Exporter Excel {selectedInvoices.size > 0 ? `(${selectedInvoices.size})` : ''}</span>
            <span className="sm:hidden">Export</span>
          </Button>
          <Button 
            onClick={handleExportPDF}
            className="btn-primary bg-primary text-primary-foreground flex-1 sm:flex-none"
            data-testid="export-pdf-btn"
            size="sm"
          >
            <FileText className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Exporter PDF {selectedInvoices.size > 0 ? `(${selectedInvoices.size})` : ''}</span>
            <span className="sm:hidden">PDF</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border">
        <CardContent className="p-3 sm:p-4">
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 sm:gap-3">
            <div className="relative col-span-2 sm:flex-1 sm:min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par N°, client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="invoice-search-input"
              />
            </div>
            
            <MultiSelectFilter
              values={typeFilter}
              onValuesChange={setTypeFilter}
              options={["Facture", "Avoir"]}
              allLabel="Tous types"
              placeholder="Types"
              className="w-full sm:w-32"
              data-testid="type-filter"
            />
            
            <Select value={yearFilter} onValueChange={(v) => {
              setYearFilter(v);
              // Reset month filter when year changes to "all"
              if (v === 'all') setMonthFilter('all');
            }}>
              <SelectTrigger className="w-full sm:w-28" data-testid="year-filter">
                <SelectValue placeholder="Année" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes années</SelectItem>
                {years.map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={monthFilter} onValueChange={setMonthFilter}>
              <SelectTrigger className="w-full sm:w-32" data-testid="month-filter">
                <SelectValue placeholder="Mois" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous mois</SelectItem>
                {months.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <MultiSelectFilter
              values={clientFilter}
              onValuesChange={setClientFilter}
              options={allClients}
              allLabel="Tous les clients"
              placeholder="Clients"
              className="w-full sm:w-48"
              data-testid="client-filter"
            />
            
            <MultiSelectFilter
              values={categoryFilter}
              onValuesChange={setCategoryFilter}
              options={categories.map(cat => cat.id)}
              allLabel="Toutes catégories"
              placeholder="Catégories"
              className="w-full sm:w-52"
              data-testid="category-filter"
            />
            
            <Select value={paiementFilter} onValueChange={setPaiementFilter}>
              <SelectTrigger className="w-full sm:w-36" data-testid="paiement-filter">
                <SelectValue placeholder="Paiement" />
              </SelectTrigger>
              <SelectContent>
                {paiementStatuts.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {hasFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters}
                className="text-muted-foreground hover:text-foreground col-span-2 sm:col-span-1"
              >
                <X className="w-4 h-4 mr-1" />
                Effacer
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 sm:p-6 space-y-3">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="h-12 skeleton rounded"></div>
              ))}
            </div>
          ) : (
            <>
              {/* Mobile Cards View */}
              <div className="block sm:hidden divide-y">
                {invoices.map((invoice, index) => (
                  <div 
                    key={invoice.numero}
                    className="p-3 hover:bg-slate-50 cursor-pointer active:bg-slate-100"
                    onClick={() => handleInvoiceClick(invoice)}
                    data-testid={`invoice-card-${index}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {invoice.has_pdf && (
                            <Paperclip className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                          )}
                          <span className="font-mono text-sm text-blue-600">{invoice.numero}</span>
                          <Badge 
                            variant="outline" 
                            className={`text-[10px] ${invoice.type === 'Facture' ? 'status-facture' : 'status-avoir'}`}
                          >
                            {invoice.type}
                          </Badge>
                        </div>
                        <p className="text-sm text-foreground mt-1 truncate">{invoice.client}</p>
                        {invoice.sous_client && (
                          <p className="text-xs text-blue-600 truncate">↳ {invoice.sous_client}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-0.5">{formatDate(invoice.date)}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-mono font-bold text-sm">{formatCurrency(invoice.total_ht)}</p>
                        <p className="text-xs text-muted-foreground">TTC: {formatCurrency(invoice.total_ttc)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
              <Table className="min-w-[700px]">
                <TableHeader>
                  <TableRow className="bg-slate-50/80 border-b border-slate-200">
                    <TableHead className="w-10">
                      <button
                        onClick={toggleSelectAll}
                        className="p-1 hover:bg-slate-200 rounded"
                        data-testid="select-all-checkbox"
                      >
                        {selectedInvoices.size === invoices.length && invoices.length > 0 ? (
                          <CheckSquare className="w-4 h-4 text-primary" />
                        ) : (
                          <Square className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider w-8"></TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider whitespace-nowrap">N° Facture</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider whitespace-nowrap">Type</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider whitespace-nowrap">Client</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider whitespace-nowrap">Date</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider whitespace-nowrap hidden md:table-cell">Échéance</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider text-right whitespace-nowrap">Total HT</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider text-right whitespace-nowrap hidden md:table-cell">TVA</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider text-right whitespace-nowrap">Total TTC</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider whitespace-nowrap">Statut Bancaire</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice, index) => (
                    <TableRow 
                      key={invoice.numero}
                      className={`cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100 ${selectedInvoices.has(invoice.numero) ? 'bg-blue-50' : ''}`}
                      onClick={() => handleInvoiceClick(invoice)}
                      data-testid={`invoice-row-${index}`}
                    >
                      <TableCell className="w-10">
                        <button
                          onClick={(e) => toggleSelectInvoice(invoice.numero, e)}
                          className="p-1 hover:bg-slate-200 rounded"
                          data-testid={`select-invoice-${index}`}
                        >
                          {selectedInvoices.has(invoice.numero) ? (
                            <CheckSquare className="w-4 h-4 text-primary" />
                          ) : (
                            <Square className="w-4 h-4 text-slate-400" />
                          )}
                        </button>
                      </TableCell>
                      <TableCell className="w-8 pr-0">
                        {invoice.has_pdf && (
                          <Paperclip className="w-4 h-4 text-blue-500" title="PDF disponible" />
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-blue-600 whitespace-nowrap">{invoice.numero}</TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${invoice.type === 'Facture' ? 'status-facture' : 'status-avoir'}`}
                        >
                          {invoice.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] text-sm" title={invoice.client}>
                        <div className="truncate">{invoice.client}</div>
                        {invoice.sous_client && (
                          <div className="text-xs text-blue-600 truncate">↳ {invoice.sous_client}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{formatDate(invoice.date)}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap hidden md:table-cell">{formatDate(invoice.echeance)}</TableCell>
                      <TableCell className="font-mono text-sm text-right whitespace-nowrap">{formatCurrency(invoice.total_ht)}</TableCell>
                      <TableCell className="font-mono text-sm text-right text-muted-foreground whitespace-nowrap hidden md:table-cell">{formatCurrency(invoice.total_tva)}</TableCell>
                      <TableCell className="font-mono text-sm text-right font-medium whitespace-nowrap">{formatCurrency(invoice.total_ttc)}</TableCell>
                      <TableCell>
                        <BankingStatusBadge
                          statut_paiement={invoice.statut_paiement}
                          rapprochement_status={invoice.rapprochement_status}
                          echeance={invoice.echeance}
                          compact={true}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
              
              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between p-3 sm:p-4 border-t gap-3">
                <div className="flex items-center gap-2 sm:gap-4">
                  <p className="text-sm text-muted-foreground">
                    <span className="hidden sm:inline">Page </span>{page}/{pages}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground hidden sm:inline">Afficher</span>
                    <Select value={String(limit)} onValueChange={(v) => setLimit(v === 'all' ? 'all' : Number(v))}>
                      <SelectTrigger className="w-20 sm:w-24 h-8" data-testid="limit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="200">200</SelectItem>
                        <SelectItem value="all">Tout</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(pages, p + 1))}
                    disabled={page === pages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Invoice Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl bg-white">
          <SheetHeader>
            <SheetTitle className="text-foreground flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {selectedInvoice?.numero}
              {selectedInvoice?.has_pdf && (
                <Paperclip className="w-4 h-4 text-blue-500" />
              )}
            </SheetTitle>
          </SheetHeader>
          
          {selectedInvoice && (
            <div className="mt-6 space-y-6">
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setSheetOpen(false);
                    setEditModalOpen(true);
                  }}
                  data-testid="edit-invoice-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  onClick={() => {
                    setSheetOpen(false);
                    setDeleteDialogOpen(true);
                  }}
                  data-testid="delete-invoice-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </div>

              {/* Invoice Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Client</p>
                  <p className="text-sm font-medium text-foreground">{selectedInvoice.client}</p>
                </div>
                {selectedInvoice.sous_client && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Sous-client</p>
                    <p className="text-sm font-medium text-foreground">{selectedInvoice.sous_client}</p>
                  </div>
                )}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Type</p>
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${selectedInvoice.type === 'Facture' ? 'status-facture' : 'status-avoir'}`}
                  >
                    {selectedInvoice.type}
                  </Badge>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Date</p>
                  <p className="text-sm font-medium text-foreground">{formatDate(selectedInvoice.date)}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Échéance</p>
                  <p className="text-sm font-medium text-foreground">{formatDate(selectedInvoice.echeance)}</p>
                </div>
              </div>

              {/* Notes */}
              {selectedInvoice.notes && (
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm text-foreground">{selectedInvoice.notes}</p>
                </div>
              )}

              {/* Totals */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total HT</p>
                    <p className="text-lg font-mono font-medium text-foreground">{formatCurrency(selectedInvoice.total_ht)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">TVA</p>
                    <p className="text-lg font-mono font-medium text-muted-foreground">{formatCurrency(selectedInvoice.total_tva)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total TTC</p>
                    <p className="text-lg font-mono font-bold text-blue-600">{formatCurrency(selectedInvoice.total_ttc)}</p>
                  </div>
                </div>
              </div>

              {/* Banking Status Section */}
              <div className={`p-4 rounded-lg border ${
                selectedInvoice.statut_paiement === 'payee' 
                  ? 'bg-green-50 border-green-200' 
                  : selectedInvoice.echeance && new Date(selectedInvoice.echeance) < new Date()
                    ? 'bg-orange-50 border-orange-200'
                    : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-2">
                    <Landmark className="w-4 h-4" />
                    Statut Bancaire
                  </p>
                  <BankingStatusBadge
                    statut_paiement={selectedInvoice.statut_paiement}
                    rapprochement_status={selectedInvoice.rapprochement_status}
                    echeance={selectedInvoice.echeance}
                  />
                </div>
                
                {selectedInvoice.bridge_transaction_id ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Transaction liée</span>
                      <span className="font-mono text-xs">{selectedInvoice.bridge_transaction_id}</span>
                    </div>
                    {selectedInvoice.date_paiement && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Date encaissement</span>
                        <span className="text-green-600 font-medium">{formatDate(selectedInvoice.date_paiement)}</span>
                      </div>
                    )}
                    {selectedInvoice.libelle_releve && (
                      <div className="pt-2 border-t border-green-200">
                        <p className="text-xs text-muted-foreground mb-1">Libellé bancaire</p>
                        <p className="font-medium text-sm">{selectedInvoice.libelle_releve}</p>
                      </div>
                    )}
                    <a 
                      href={`/rapprochement-v2/transactions?id=${selectedInvoice.bridge_transaction_id}`}
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs mt-2"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Voir la transaction
                    </a>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-sm text-muted-foreground mb-2">Aucune transaction liée</p>
                    <a 
                      href={`/rapprochement-v2/pending`}
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      <Search className="w-3 h-3" />
                      Chercher une transaction
                    </a>
                  </div>
                )}
              </div>

              {/* PDF Section */}
              {selectedInvoice.has_pdf ? (
                <Button
                  onClick={handleViewPDF}
                  className="w-full btn-primary bg-blue-600 hover:bg-blue-700 text-white h-12"
                  data-testid="view-pdf-btn"
                >
                  <Eye className="w-5 h-5 mr-2" />
                  Voir la facture PDF
                </Button>
              ) : (
                <div className="p-4 bg-gray-50 rounded-lg border border-dashed border-gray-200 text-center">
                  <Paperclip className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Aucun PDF associé</p>
                </div>
              )}

              {/* Lines */}
              {selectedInvoice.lines && selectedInvoice.lines.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-3">Lignes de facturation</h4>
                  <ScrollArea className="h-[200px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs uppercase text-muted-foreground">Description</TableHead>
                          <TableHead className="text-xs uppercase text-muted-foreground text-center">Qté</TableHead>
                          <TableHead className="text-xs uppercase text-muted-foreground text-right">P.U. HT</TableHead>
                          <TableHead className="text-xs uppercase text-muted-foreground text-right">Montant HT</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedInvoice.lines.map((line, index) => (
                          <TableRow key={line.description + index}>
                            <TableCell className="text-sm max-w-[180px]">
                              <span className="line-clamp-2" title={line.description}>
                                {line.description}
                              </span>
                            </TableCell>
                            <TableCell className="font-mono text-sm text-center">{line.quantity}</TableCell>
                            <TableCell className="font-mono text-sm text-right">{formatCurrency(line.unit_price)}</TableCell>
                            <TableCell className="font-mono text-sm text-right">{formatCurrency(line.amount_ht || line.total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Import Excel Modal */}
      <ImportModal 
        open={importModalOpen} 
        onOpenChange={setImportModalOpen}
        onSuccess={fetchInvoices}
      />

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        open={pdfViewerOpen}
        onOpenChange={setPdfViewerOpen}
        invoiceNumero={selectedInvoice?.numero}
        invoiceClient={selectedInvoice?.client}
      />

      {/* Invoice Edit Modal */}
      <InvoiceEditModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        invoice={selectedInvoice}
        onSuccess={() => {
          fetchInvoices();
          // Refresh selected invoice data
          if (selectedInvoice?.numero) {
            axios.get(`${API}/invoices/${encodeURIComponent(selectedInvoice.numero)}`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(res => {
              setSelectedInvoice(res.data);
            }).catch(err => console.error(err));
          }
        }}
      />

      {/* OCR Upload Modal */}
      <OCRUploadModal
        open={ocrUploadOpen}
        onOpenChange={setOcrUploadOpen}
        onSuccess={fetchInvoices}
      />

      {/* Delete Invoice Dialog */}
      <DeleteInvoiceDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        invoice={selectedInvoice}
        onSuccess={() => {
          fetchInvoices();
          setSelectedInvoice(null);
        }}
      />
    </div>
  );
}
